import React, { useEffect, useState } from 'react'
import { color } from 'web/src/style/variables'
import { Content, H1Header, LoginBox, Page, PageLoader, Picture, ToggleSlider } from '@/components'
import { media } from 'web/src/style/media'
import { optimizeContentfulImageUrl } from 'web/src/helpers/images'
import { useGetStore } from 'web/src/redux/hooks/hooks'

import { useSearchParams } from 'react-router-dom'
import { ErrorPage404 } from '@/pages/ErrorPage404/ErrorPage404'
import { XStack } from '@red-ui/components'
import { InfinateRewardsList } from './InfinateRewardsList'
import { BrandDetails } from '@vrw/data/src/redux/rewards/types'
import useInfiniteEarnRewards from '@/query/rewards/useInfiniteEarnRewards'
import useInfiniteSpendRewards from '@/query/rewards/useInfiniteSpendRewards'
import { RewardsSort as RewardSortSelect } from '@/components/DiscoveryFeed/Sort/Sort'
import { RewardsSort } from '@vrw/data/src/redux/types'
import { getString } from '@vrw/data'

export type EarnOrSpend = 'earn' | 'rewards'
export type SortOption = RewardsSort | 'featured' | 'blank'

type BrandDetailsPageContentProps = { brand: BrandDetails }

const BrandDetailsPageContent = ({ brand: { brandName, brandDescription, brandLogo, slug: brandSlug } }: BrandDetailsPageContentProps) => {
  const [showToggleSlider, setShowToggleSlider] = useState(false)
  const isAuthenticated = useGetStore('auth.isAuthenticated')
  const [selectedTab, setSelectedTab] = useState<EarnOrSpend | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [sortOption, setSortOption] = useState<SortOption>((searchParams.get('sort') as SortOption) || 'featured')

  // Convert to use for API calls, undefined for featured gives default backend sorting
  const apiSortValue = sortOption === 'featured' || sortOption === 'blank' ? undefined : (sortOption as RewardsSort)

  const tabs = [
    { key: 'earn', name: 'Earn', onClick: () => setSelectedTab('earn') },
    { key: 'rewards', name: 'Rewards', onClick: () => setSelectedTab('rewards') },
  ]

  const {
    rewards: earnRewards,
    isPending: isPendingEarnRewards,
    isError: isErrorEarnRewards,
    fetchNextPage: fetchNextPageEarnRewards,
  } = useInfiniteEarnRewards({ brandName, sort: apiSortValue }, [sortOption])

  const {
    rewards: spendRewards,
    isPending: isPendingSpendRewards,
    isError: isErrorSpendRewards,
    fetchNextPage: fetchNextPageSpendRewards,
  } = useInfiniteSpendRewards({ brandName, sort: apiSortValue }, [sortOption])

  useEffect(() => {
    let hasEarnRewards = false
    let hasSpendRewards = false
    let hasEarnAndSpendRewards = false

    if (earnRewards && spendRewards) {
      if (earnRewards.pages[0].length > 0) hasEarnRewards = true
      if (spendRewards.pages[0].length > 0) hasSpendRewards = true
      hasEarnAndSpendRewards = hasEarnRewards && hasSpendRewards
    }

    if (hasEarnAndSpendRewards) {
      setShowToggleSlider(true)
      if (selectedTab === null) setSelectedTab('earn')
    } else {
      if (hasSpendRewards) setSelectedTab('rewards')
      if (hasEarnRewards) setSelectedTab('earn')
    }
  }, [earnRewards, spendRewards, selectedTab])

  if (isPendingEarnRewards || isPendingSpendRewards) {
    return (
      <Page>
        <PageLoader />
      </Page>
    )
  }

  if (isErrorEarnRewards || isErrorSpendRewards || !earnRewards || !spendRewards) {
    return <ErrorPage404 />
  }

  const updateSortOption = (newSortOption: SortOption) => {
    setSortOption(newSortOption)
    setSearchParams({ ...Object.fromEntries(searchParams), sort: newSortOption }, { replace: true })
  }

  const brandSortOptions = [
    { key: undefined, label: getString('brands.sort.featured') },
    { key: RewardsSort.CostAsc, label: getString('brands.sort.pointsLowest') },
    { key: RewardsSort.CostDesc, label: getString('brands.sort.pointsHighest') },
    { key: RewardsSort.DateAsc, label: getString('brands.sort.dateClosest') },
    { key: RewardsSort.DateDesc, label: getString('brands.sort.dateFurthest') },
  ]

  return (
    <>
      <style jsx>{`
        .copy {
          font-size: 18px;
          margin: 0;
        }
        .heading {
          display: flex;
          flex-flow: wrap;
          margin-bottom: 32px;
        }
        .logo {
          box-shadow: rgba(0, 0, 0, 0.15) 0px 2px 4px 0px;
          width: 144px;
          height: 144px;
          flex-shrink: 0;
          margin: 32px auto 0;
        }
        .login-banner {
          margin-bottom: 34px;
        }
        .login-banner :global(.header-two) {
          margin-top: 0;
        }
        .login-banner :global(.reward-cta-block) {
          padding: 40px 0 56px 0;
        }
        .brand-message {
          font-weight: 600;
          font-size: 28px;
        }
        @media ${media.tabletAndHigher} {
          .heading {
            flex-flow: row;
          }
          .copy {
            font-size: 16px;
          }
          .content {
            margin-left: 40px;
            flex-grow: 1;
          }
          .login-banner {
            margin-bottom: 42px;
          }
          .login-banner :global(.reward-cta-block) {
            padding: 64px 0;
          }
          .login-banner :global(h2),
          .login-banner :global(p),
          .login-banner :global(.reward-cta-actions) {
            max-width: 100%;
          }
          .login-banner :global(.cta-action:first-child) {
            margin-bottom: 0;
          }
        }
        @media ${media.desktopAndHigher} {
          .copy {
            font-size: 20px;
            max-width: 70%;
          }
          .heading {
            margin-bottom: 72px;
          }
          .logo {
            width: 160px;
            height: 160px;
          }
          .login-banner {
            margin-bottom: 34px;
          }
          .login-banner :global(h2),
          .login-banner :global(p),
          .login-banner :global(.reward-cta-actions) {
            max-width: 1000px;
          }
        }
      `}</style>
      <Page title={brandName}>
        <Content>
          <div className="heading">
            <div className="logo">
              <Picture
                altText={`${brandName} logo`}
                className="responsive-img--override"
                width={160}
                fallbackImg={optimizeContentfulImageUrl(brandLogo, 'fill', { width: 144, height: 144 })}
                height={160}
                responsiveImages={{
                  mobile: {
                    imgSrc: optimizeContentfulImageUrl(brandLogo, 'fill', { width: 144, height: 144 }),
                    hiResImgSrc: optimizeContentfulImageUrl(brandLogo, 'fill', { width: 144, height: 144 }),
                  },
                }}
                loading={'lazy'}
                isWebP={true}
              />
            </div>
            <div className="content">
              <H1Header
                color={color.textHeader}
                fontSize={{ desktop: 40, tablet: 28, mobile: 28 }}
                fontStyle="normal"
                marginTop={{ desktop: 32, tablet: 32, mobile: 32 }}
                marginBottom={{ desktop: 16, tablet: 16, mobile: 8 }}
                weight={600}>
                {brandName}
              </H1Header>
              <p className="copy">{brandDescription}</p>
            </div>
          </div>
        </Content>
        {!isAuthenticated && (
          <div className="login-banner">
            <LoginBox isEarn />
          </div>
        )}
        <Content>
          {!!selectedTab &&
            (showToggleSlider ? (
              <ToggleSlider tabs={tabs} selected={selectedTab} />
            ) : (
              <h2 className="brand-message">{`${selectedTab === 'rewards' ? 'Spend' : 'Earn'} points with ${brandName}`}</h2>
            ))}
          {brandSlug === 'virgin-voyages' && (
            <XStack justifyContent="flex-end">
              <RewardSortSelect
                sort={sortOption === 'featured' || sortOption === 'blank' ? undefined : sortOption}
                setSort={(newSort) => updateSortOption(newSort ?? 'featured')}
                sortOptions={brandSortOptions}
              />
            </XStack>
          )}
          {selectedTab === 'earn' ? (
            <InfinateRewardsList
              fetchNextPage={fetchNextPageEarnRewards}
              delayRender={false}
              isLoading={isPendingEarnRewards}
              rewards={earnRewards}
            />
          ) : (
            <InfinateRewardsList
              fetchNextPage={fetchNextPageSpendRewards}
              delayRender={false}
              isLoading={isPendingSpendRewards}
              rewards={spendRewards}
            />
          )}
        </Content>
      </Page>
    </>
  )
}

export default BrandDetailsPageContent
